// TypingIndicator — 20px strip above the composer that shows who is
// currently typing in the active room.
//
// The copy rules are brief-pinned:
//   1 typer:  "<displayName> is typing…"
//   2 typers: "<a> and <b> are typing…"
//   3+:       "Several people are typing…"
//
// Animated dots (three 4×4 circles) bounce in a 600ms loop, staggered
// 100ms per dot, and are disabled under `prefers-reduced-motion: reduce`
// — the dots render static under that media query. The keyframes live
// in a per-module injected stylesheet (same pattern as RoomListItem)
// so the component stays declarative.
//
// The component takes `userIds` + a `resolveName` function rather than
// subscribing itself. The embedder (Timeline) owns the subscription —
// it already knows the active room and the members map, and passing
// the resolved list keeps this leaf component renderable from stories
// with any fixture.

import type { CSSProperties } from "react";

const STYLE_ID = "chat-typing-indicator-styles";
const DOT_CLASS = "chat-typing-dot";

function injectStyles(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  // 600ms loop, three dots staggered 100ms each. `translateY(-3px)` at
  // the peak reads as a gentle bounce without fighting the 4×4 dot
  // size. Scoped via a single class so a second mount doesn't
  // re-register keyframes.
  style.textContent = `
    @keyframes chat-typing-dot-bounce {
      0%, 80%, 100% { transform: translateY(0); }
      40% { transform: translateY(-3px); }
    }
    .${DOT_CLASS} {
      animation: chat-typing-dot-bounce 600ms var(--chat-motion-ease) infinite;
    }
    .${DOT_CLASS}[data-index="1"] { animation-delay: 100ms; }
    .${DOT_CLASS}[data-index="2"] { animation-delay: 200ms; }
    @media (prefers-reduced-motion: reduce) {
      .${DOT_CLASS} { animation: none; }
    }
  `;
  document.head.appendChild(style);
}
injectStyles();

export interface TypingIndicatorProps {
  /** User ids currently typing in the active room. Empty = no strip. */
  userIds: string[];
  /**
   * Resolve a user id to a display name. The embedder (Timeline)
   * sources this from the SDK's member map — keeping resolution
   * outside the component lets every story render any id.
   */
  resolveName: (userId: string) => string;
}

function composeCopy(userIds: string[], resolveName: (id: string) => string): string {
  // `userIds` is assumed de-duplicated by the subscriber (Matrix's
  // typing event is a set by semantics; the stub uses `Set` too). No
  // defensive dedup here — the brief's copy rules count distinct
  // typers, and a duplicate would be a bug in the subscription, not a
  // case this component should paper over.
  if (userIds.length === 0) return "";
  if (userIds.length === 1) return `${resolveName(userIds[0]!)} is typing…`;
  if (userIds.length === 2) {
    return `${resolveName(userIds[0]!)} and ${resolveName(userIds[1]!)} are typing…`;
  }
  return "Several people are typing…";
}

export function TypingIndicator({ userIds, resolveName }: TypingIndicatorProps) {
  const copy = composeCopy(userIds, resolveName);
  // Always render the container so the 20px strip doesn't push the
  // composer up/down when the indicator appears or disappears — the
  // composer border stays anchored and the copy fades in/out inside
  // the reserved strip. `aria-live="polite"` announces typers without
  // interrupting screen-reader flow.
  const containerStyle: CSSProperties = {
    height: 20,
    padding: "0 var(--chat-space-3) 0 var(--chat-space-4)",
    display: "flex",
    alignItems: "center",
    gap: "var(--chat-space-2)",
    color: "var(--chat-fg-secondary)",
    fontFamily: "var(--chat-font-sans)",
    fontSize: "var(--chat-type-body-sm-size)",
    lineHeight: "var(--chat-type-body-sm-line)",
    overflow: "hidden",
    whiteSpace: "nowrap",
    // Fade the copy in/out rather than the container so the 20px
    // height stays reserved — no layout jitter above the composer.
    opacity: userIds.length > 0 ? 1 : 0,
    transition: "opacity var(--chat-motion-fast) var(--chat-motion-ease)",
  };
  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      aria-label="Typing"
      style={containerStyle}
      data-cy="chat-awareness"
      data-testid="typing-indicator"
    >
      {userIds.length > 0 ? (
        <>
          <Dots />
          <span
            style={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {copy}
          </span>
        </>
      ) : null}
    </div>
  );
}

function Dots() {
  // 4×4 dots, 4px gap, all in `fg-secondary`. `aria-hidden` so the
  // screen reader reads only the copy (the live region announces
  // "Alice is typing" not "Alice is typing bullet bullet bullet").
  const dotStyle: CSSProperties = {
    width: 4,
    height: 4,
    borderRadius: "var(--chat-radius-full)",
    background: "var(--chat-fg-secondary)",
    flexShrink: 0,
  };
  return (
    <span
      aria-hidden
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        flexShrink: 0,
      }}
    >
      <span className={DOT_CLASS} data-index="0" style={dotStyle} />
      <span className={DOT_CLASS} data-index="1" style={dotStyle} />
      <span className={DOT_CLASS} data-index="2" style={dotStyle} />
    </span>
  );
}
